import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';
import { compose } from 'redux';
import { withTheme } from '@material-ui/core/styles';
import { withDynamicImports } from 'Components/withDynamicImports.hoc';

import { TestID } from '../../testID';
import { buildEntityDifferences } from '../../utils/entities.utils';

import './JsonCompare.css';

const CODESTYLE_KEY = 'sesam-editor-codestyle';

const getFontSize = () => {
  const codeStyleOverrides = JSON.parse(localStorage?.getItem(CODESTYLE_KEY) || '{}');
  return codeStyleOverrides?.font_size ?? 12;
};

/*
 * Panel that displays diff between two JSON files.
 */
class JsonCompare extends React.Component {
  constructor() {
    super();

    const filter = (value) => pickBy(value, (val, key) => !key.startsWith('_'));

    const format = (value) => JSON.stringify(value === undefined ? {} : value, null, 2) || '{}';

    this.destroyDiffer = () => {
      if (this.differ && this.differ.destroy) {
        this.differ.destroy();
        this.differ = null;
      }
    };

    this.getCurrentAceTheme = () => {
      if (this.props.theme.palette.type === 'dark') {
        return 'ace/theme/dracula';
      } else {
        return 'ace/theme/textmate';
      }
    };

    this.createDiffer = () => {
      this.destroyDiffer();

      const filterValue = (value) => (this.props.filter ? filter(value) : value);
      const leftValue = filterValue(this.props.value?.data ?? {});
      const rightValue = filterValue(this.props.original?.data ?? {});

      let leftContent = leftValue;
      let rightContent = rightValue;

      if (this.props.showDifferencesOnly) {
        const [leftDiff, rightDiff] = buildEntityDifferences(leftValue, rightValue);
        leftContent = leftDiff ?? {};
        rightContent = rightDiff ?? {};
      }

      this.differ = new this.props.imports.AceDiff({
        mode: 'ace/mode/json',
        theme: this.getCurrentAceTheme(),
        left: {
          element: this.leftEditor,
          content: format(leftContent),
          editable: false,
          copyLinkEnabled: false,
        },
        right: {
          element: this.rightEditor,
          content: format(rightContent),
          editable: false,
          copyLinkEnabled: false,
        },
        gutterEl: this.gutter,
      });

      if (this.differ?.editors?.left?.ace) {
        this.differ.editors.left.ace.setOptions({
          showPrintMargin: false,
          fontSize: getFontSize(),
        });
      }

      if (this.differ?.editors?.right?.ace) {
        this.differ.editors.right.ace.setOptions({
          showPrintMargin: false,
          fontSize: getFontSize(),
        });
      }
    };
  }

  componentDidMount() {
    this.createDiffer();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props, prevProps)) {
      this.createDiffer();
    }
  }

  componentWillUnmount() {
    this.destroyDiffer();
  }

  render() {
    // need a key so that we create new dom elements for each AceDiff
    // TODO: This is pretty stupid way of doing things
    const key = `${this.props.value.key}-${this.props.original.key}-${this.props.filter}-${this.props.showDifferencesOnly}-${this.props.theme.palette.type}`;
    return (
      <div key={key} className="json-compare" data-testid={TestID.JsonComparePanel}>
        <div
          className="acediff-left-editor"
          data-testid={TestID.JsonCompareLeftEditor}
          ref={(e) => {
            this.leftEditor = e;
          }}
        />
        <div
          className="acediff-gutter"
          ref={(e) => {
            this.gutter = e;
          }}
        />
        <div
          className="acediff-right-editor"
          data-testid={TestID.JsonCompareRightEditor}
          ref={(e) => {
            this.rightEditor = e;
          }}
        />
      </div>
    );
  }
}

JsonCompare.propTypes = {
  value: PropTypes.shape({
    data: PropTypes.shape({}).isRequired,
    key: PropTypes.number.isRequired,
  }).isRequired,
  original: PropTypes.shape({
    data: PropTypes.shape({}).isRequired,
    key: PropTypes.number.isRequired,
  }).isRequired,
  filter: PropTypes.bool,
  showDifferencesOnly: PropTypes.bool,
};

const lazyImports = {
  braceModeJson: () => import('brace/mode/json'),
  braceThemeDracula: () => import('brace/theme/dracula'),
  AceDiff: () => import('brace-diff'),
};

export default compose(
  withTheme,
  withDynamicImports(lazyImports),
)(JsonCompare);
